import { Button, Divider, Flex, Typography } from "antd";
import useFinishScreenStyles from "./FinishScreen.styles";
import { useFormContext, useWatch } from "react-hook-form";
import type { FormValues } from "../../model/form.schema";
import { useSteps } from "@/shared/hooks/useGetSteps";
import useGetBranch from "@/shared/hooks/useGetBranch";
import dayjs from "dayjs";
import { useMemo } from "react";
import useGetEmployeeSchedule from "@/shared/hooks/useGetEmployeeSchedule";
import { Header } from "@/features/header";
import { DefaultStepValues } from "@/shared/lib/step.constants";

const FinishScreen = () => {
  const { styles, cx } = useFinishScreenStyles();

  const { setSteps } = useSteps();

  const { control, reset } = useFormContext<FormValues>();
  const { branchData } = useGetBranch();
  const branchId = Number(location.pathname.split("/").at(-1));

  const formValues = useWatch({ control });
  const date = useWatch({ control, name: "date" });

  const { employeeScheduleData } = useGetEmployeeSchedule({
    date: date || dayjs(),
    duration: formValues.duration || branchData?.branch.online_booking_durations[0] || 30,
  });

  const currEmployee = useMemo(
    () => employeeScheduleData?.employees.find((emp) => emp.employee.id === formValues.employee_id),
    [formValues.employee_id, employeeScheduleData],
  );

  const handleGoMain = () => {
    setSteps(
      DefaultStepValues.schedulFirst.map((step) => ({
        ...step,
        status: "wait",
      })),
    );
    reset({
      duration: branchData?.branch.online_booking_durations[0] || 30,
      date: dayjs(),
      branch_id: branchId,
    });
  };

  return (
    <Flex className={styles.screen} vertical gap={8}>
      <Header />
      <Flex className="body" vertical>
        <Flex gap={25} className="iconBody" vertical justify="center" align="center">
          <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none">
            <path
              d="M38.3307 12.459L17.2474 33.5423L7.66406 23.959"
              stroke="#1677FF"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <Typography.Title level={5} className="iconText">
            Вы успешно забронированы!
          </Typography.Title>
        </Flex>

        <Flex className="details" vertical gap={12}>
          <Divider className="divider" />

          <Flex className="detailsItem" gap={8} align="center">
            <svg
              className="icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="22"
              viewBox="0 0 20 22"
              fill="none"
            >
              <path
                d="M5.75 0.75V4.75"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.75 0.75V4.75"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M16.75 2.75H2.75C1.64543 2.75 0.75 3.64543 0.75 4.75V18.75C0.75 19.8546 1.64543 20.75 2.75 20.75H16.75C17.8546 20.75 18.75 19.8546 18.75 18.75V4.75C18.75 3.64543 17.8546 2.75 16.75 2.75Z"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M0.75 8.75H18.75"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.75 12.75H5.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9.75 12.75H9.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.75 12.75H13.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.75 16.75H5.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9.75 16.75H9.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.75 16.75H13.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <Flex vertical className="detailsBody">
              <Typography.Text className="detailsTitle">
                {formValues.start_time} - {formValues.end_time}
              </Typography.Text>
              <Typography.Text className="detailsText">{date.format("dddd, D MMMM")}</Typography.Text>
            </Flex>
          </Flex>
          <Divider className="divider" />

          <Flex className="detailsItem" gap={8} align="center">
            <svg
              className="icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="22"
              viewBox="0 0 20 22"
              fill="none"
            >
              <path
                d="M5.75 0.75V4.75"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.75 0.75V4.75"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M16.75 2.75H2.75C1.64543 2.75 0.75 3.64543 0.75 4.75V18.75C0.75 19.8546 1.64543 20.75 2.75 20.75H16.75C17.8546 20.75 18.75 19.8546 18.75 18.75V4.75C18.75 3.64543 17.8546 2.75 16.75 2.75Z"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M0.75 8.75H18.75"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.75 12.75H5.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9.75 12.75H9.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.75 12.75H13.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5.75 16.75H5.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9.75 16.75H9.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M13.75 16.75H13.76"
                stroke="#1677FF"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <Flex vertical className="detailsBody">
              <Typography.Text className="detailsTitle">
                {cx(
                  currEmployee?.employee.first_name,
                  currEmployee?.employee.middle_name,
                  currEmployee?.employee.last_name,
                )}
              </Typography.Text>
              <Typography.Text className="detailsText">
                {currEmployee?.employee.specialties
                  .map((sp) => sp.name)
                  .filter((i) => i.trim())
                  .join(", ")}
              </Typography.Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Flex className={cx("full-width", styles.footer)}>
        <Button type="primary" className="full-width" htmlType="submit" onClick={handleGoMain}>
          Вернуться на главную
        </Button>
      </Flex>{" "}
    </Flex>
  );
};
export default FinishScreen;
